import React from 'react'
import { graphql } from 'gatsby'
// components
import SectionHeader from '../components/Headers/SectionHeader'
import PageWrap from '../components/Layouts/PageWrap'
import Offset from '../components/Layouts/Offset'
import SectionContainer from '../components/Layouts/SectionContainer'
import BulletList from '../components/Lists/BulletList'
import CardHorizontal from '../components/Cards/CardHorizontal'
import CardInfo from '../components/Cards/CardInfo'
import Testimonial from '../components/Testimonials/TestimonialsSwiper'
import ContactForm from '../components/ContactForm'

import Image from '../components/Images/Image'

// layouts
import ThreeUp from '../components/Layouts/ThreeUp'

import { getImageBySize } from '../components/Images/Image'

const ServiceSection = ({ heading, content, bullets, cards }) => (
  <div>
    <SectionHeader heading={heading} />
    <div className="my-12">
      <div className="lg:grid grid-cols-2 col-gap-10 text-gray-600">
        <div className="mb-12 lg:mb-0">
          <p class="text-lg">{content}</p>
        </div>
        <div>
          <BulletList className="sm:mc-col-count-2 mc-col-gap-16">
            {bullets && bullets.map((b, idx) => <li key={idx}>{b}</li>)}
          </BulletList>
        </div>
      </div>
    </div>
    <div className="md:w-3/4">
      {cards.map((card, idx) =>
        card.heading != null ? (
          <div className="mb-12">
            <CardHorizontal key={idx} {...card}></CardHorizontal>
          </div>
        ) : (
          ''
        )
      )}
    </div>
  </div>
)

const Services = args => {
  const openingData = {
    heading: args.data?.page?.title,
    subheading: args.data?.page?.content,
  }

  const services = args.data?.page?.acf?.services.map(service => {
    return {
      heading: service.serviceTitle,
      content: service.serviceDescription,
      bullets: service.serviceBulletPoints.map(bullet => bullet.bulletItem),
      layout: service.layout,
      cards: [
        {
          labels: [
            {
              label: 'Featured Success story',
              url: service.featuredSuccessStory?.uri,
            },
          ],
          heading: service.featuredSuccessStory?.title,
          subheading:
            service.featuredSuccessStory?.acfSuccessStory?.taglineShort,
          image:
            service.featuredSuccessStory?.featuredImage.node &&
            getImageBySize(
              service.featuredSuccessStory.featuredImage.node,
              'rect-medium'
            ),
          logo: {
            src:
              service.featuredSuccessStory?.acfSuccessStory?.cardLogo
                ?.mediaItemUrl,
            alt:
              service.featuredSuccessStory?.acfSuccessStory?.cardLogo?.altext,
          },
          link: {
            url: service.featuredSuccessStory?.uri,
            label: 'Read the Success Story',
          },
        },
        {
          labels: [
            {
              label: 'Featured insight',
              url: service.featuredInsight?.uri,
            },
          ],
          heading: service.featuredInsight?.title,
          subheading: service.featuredInsight?.excerpt,
          image:
            service.featuredInsight?.featuredImage?.node &&
            getImageBySize(
              service.featuredInsight.featuredImage.node,
              'rect-medium'
            ),
          link: {
            url: service.featuredInsight?.uri,
            label: 'Read the Insight',
          },
        },
      ],
    }
  })

  const testimonials = [
    {
      quote: args.data?.page?.acf?.quote?.quoteText,
      person: {
        name: args.data?.page?.acf?.quote?.quoteName,
        org: args.data?.page?.acf?.quote?.quoteByline,
      },
    },
  ]

  const engagement = {
    heading: args.data?.page?.acf?.engagements?.engagementHeader,
    content: args.data?.page?.acf?.engagements?.engagementLeadIn,
    cards: args.data?.page?.acf?.engagements?.engagementItems?.map(item => {
      return {
        heading: item.engagementTitle,
        subheading: item.engagementText,
        image: getImageBySize(item.engagementThumbnail, 'original'),
        link: {
          url: item.engagementLink?.url,
        },
      }
    }),
  }

  return (
    <PageWrap seo={args.data?.page?.seo} noFlex>
      <SectionContainer className="container">
        <SectionHeader {...openingData} as="h1" />
      </SectionContainer>
      <SectionContainer as="div">
        <Offset inline>
          <Image
            src={args.data?.page?.featuredImage?.node?.mediaItemUrl}
            alt={args.data?.page?.featuredImage?.node?.altText}
          />
        </Offset>
      </SectionContainer>

      <SectionContainer className="container">
        <SectionHeader heading={engagement.heading}></SectionHeader>
        <p className="my-10 text-xl">{engagement.content}</p>
        <ThreeUp>
          {engagement.cards &&
            engagement.cards.map((card, idx) => (
              <div>
                <CardInfo
                  {...card}
                  link=""
                  isImageFullWidth
                  key={idx}
                ></CardInfo>
              </div>
            ))}
        </ThreeUp>
      </SectionContainer>

      {services.map((service, idx) => (
        <SectionContainer
          className={`${service.layout === 'Center' ? 'container' : ''}`}
        >
          {service.layout === 'Left' && (
            <Offset direction="left">
              <div className="bg-gray-100 py-12 md:py-24 pr-5 md:pr-20">
                <Offset rounded={false}>
                  <ServiceSection {...service}></ServiceSection>
                </Offset>
              </div>
            </Offset>
          )}
          {service.layout === 'Right' && (
            <Offset direction="right">
              <div className="bg-gray-100 py-12 md:py-24 pl-5 md:pl-20">
                <Offset direction="left" rounded={false}>
                  <ServiceSection {...service}></ServiceSection>
                </Offset>
              </div>
            </Offset>
          )}
          {service.layout === 'Center' && (
            <ServiceSection {...service}></ServiceSection>
          )}
        </SectionContainer>
      ))}

      <SectionContainer
        as="div"
        className="container"
        aria-label="Testimonials"
      >
        <div className="mx-auto max-w-2xl bg-blue-200">
          <Testimonial testimonials={testimonials}></Testimonial>
        </div>
      </SectionContainer>

      <SectionContainer className="w-full p-4 lg:pl-16 max-w-2xl mx-auto">
        <SectionHeader heading="Get In Touch" />
        <ContactForm slug={args.data?.page?.slug || ''} />
      </SectionContainer>
    </PageWrap>
  )
}

export default Services

export const query = graphql`
  query($id: String) {
    page: wpPage(id: { eq: $id }) {
      slug
      seo {
        ...seoPostTypeFields
      }
      title
      content
      featuredImage {
        node {
          altText
          mediaItemUrl
        }
      }
      acf: acfService {
        services {
          serviceTitle
          serviceDescription
          layout
          serviceBulletPoints {
            bulletItem
          }
          featuredSuccessStory {
            ... on WpSuccessStory {
              title
              uri
              featuredImage {
                node {
                  ...mediaItemFields
                }
              }
              acfSuccessStory {
                taglineShort
                cardLogo {
                  mediaItemUrl
                  altText
                }
              }
            }
          }
          featuredInsight {
            ... on WpPost {
              title
              excerpt
              uri
              featuredImage {
                node {
                  ...mediaItemFields
                }
              }
            }
          }
        }
        engagements {
          engagementHeader
          engagementLeadIn
          engagementItems {
            engagementThumbnail {
              ...mediaItemFields
            }
            engagementTitle
            engagementText
            engagementLink {
              target
              url
            }
          }
        }
        quote {
          quoteText
          quoteName
          quoteByline
        }
      }
    }
  }
`
